import { Interceptor } from "@connectrpc/connect";
import { useAuth } from "@workos-inc/authkit-react";

import { getEnv } from "@/utils/env";

import { IS_DEBUG } from "@/constants";

/**
 * Get the API base URL from environment variables
 */
export function getApiBaseUrl(): string {
  return getEnv("VITE_API_URL", "http://localhost:8081");
}

/**
 * Create an interceptor for adding the Authorization header to the request
 */
export function createAuthInterceptor(
  getAccessToken: ReturnType<typeof useAuth>["getAccessToken"],
): Interceptor {
  return (next) => async (req) => {
    try {
      const token = await getAccessToken();
      if (token) {
        req.header.set("Authorization", `Bearer ${token}`);
      }
    } catch {
      // No token available - continue without auth header
    }
    return next(req);
  };
}

/**
 * Create an interceptor for request/response logging in development
 */
export function createLoggingInterceptor(): Interceptor {
  return (next) => async (req) => {
    if (IS_DEBUG) {
      console.debug("[Connect-RPC] Request:", {
        service: req.service.typeName,
        method: req.method.name,
        url: req.url,
        headers: Object.fromEntries(req.header),
      });
    }

    try {
      const res = await next(req);

      if (IS_DEBUG) {
        console.debug("[Connect-RPC] Response:", {
          service: req.service.typeName,
          method: req.method.name,
          status: "success",
        });
      }

      return res;
    } catch (error) {
      if (IS_DEBUG) {
        console.debug("[Connect-RPC] Error:", {
          service: req.service.typeName,
          method: req.method.name,
          error,
        });
      }
      throw error;
    }
  };
}
